import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import {
  SURVEY_STATUS_LABEL,
  SURVEY_STATUS_TONE,
  EMPTY_AUDIENCE_FILTER,
  applyAudienceFilter,
  computeChannelStats,
  computeMetrics,
  computeQuestionResults,
  deriveFilterOptions,
  isLiveStatus,
  stepsOf,
  submittedResponses,
  type AudienceFilter,
} from '../../lib/surveyResults';
import { buildSurveyCsv, csvFilename } from '../../lib/surveyCsv';
import { cancelSurvey, closeSurvey, getSurveyResults } from './api';
import { MetricCards } from './results/MetricCards';
import { ChannelBreakdown } from './results/ChannelBreakdown';
import { QuestionResults } from './results/QuestionResults';
import { AudienceFilterBar } from './results/AudienceFilterBar';
import { IndividualResponses } from './results/IndividualResponses';
import { ConfirmActionModal } from './results/ConfirmActionModal';
import { ResendModal } from './results/ResendModal';

// Phase 6B Phase E — survey results dashboard at /surveys/:id (distinct from
// /edit + /deleted). Single-page scroll: metrics → per-channel → per-question →
// audience filter → individual responses. Polls every 10s while the survey is
// live (sending/open). Admin/initiator only — the server 403s otherwise.
export function SurveyResultsPage() {
  const { id } = useParams<{ id: string }>();
  const results = useQuery({
    queryKey: ['survey-results', id],
    queryFn: () => getSurveyResults(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && isLiveStatus(q.state.data.survey.status) ? 10000 : false),
  });
  const [filter, setFilter] = useState<AudienceFilter>(EMPTY_AUDIENCE_FILTER);
  const [action, setAction] = useState<null | 'close' | 'cancel' | 'resend'>(null);
  const me = useCurrentUser();

  const closeMutation = useMutation({
    mutationFn: () => closeSurvey(id!),
    onSuccess: () => {
      toast.success('Survey closed.');
      setAction(null);
      void results.refetch();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not close the survey.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelSurvey(id!),
    onSuccess: () => {
      toast.success('Survey cancelled.');
      setAction(null);
      void results.refetch();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not cancel the survey.'),
  });

  if (results.isLoading) return <CenteredSpinner />;
  if (results.error) {
    const msg = results.error instanceof ApiError ? results.error.message : 'Could not load these results.';
    return (
      <div>
        <PageHeader title="Survey results" backHref="/surveys" />
        <p className="text-sm text-red-600">{msg}</p>
      </div>
    );
  }
  if (!results.data) return null;

  const { survey } = results.data;
  const metrics = computeMetrics(results.data);
  const channels = computeChannelStats(results.data);
  const submitted = submittedResponses(results.data);
  const filterOptions = deriveFilterOptions(submitted);
  const filteredResponses = applyAudienceFilter(submitted, filter);
  const questionResults = computeQuestionResults(results.data, filteredResponses);
  const live = isLiveStatus(survey.status);

  // Lifecycle permission (spec §8): admin or the survey's initiator.
  const canManage = me.role === 'admin' || survey.createdBy.uid === me.uid;
  const canClose = canManage && survey.status === 'open';
  const canCancel = canManage && ['sending', 'open', 'closed'].includes(survey.status);
  const canResend = canManage && survey.status === 'open';

  return (
    <div>
      <PageHeader
        title={survey.title}
        description={survey.description ?? undefined}
        backHref="/surveys"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => downloadCsv(csvFilename(survey.title), buildSurveyCsv(results.data!))}
              disabled={metrics.submitted === 0}
            >
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => results.refetch()} disabled={results.isFetching}>
              {results.isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
            {canClose && (
              <Button variant="outline" onClick={() => setAction('close')}>
                Close survey
              </Button>
            )}
            {canCancel && (
              <Button variant="destructive" onClick={() => setAction('cancel')}>
                Cancel survey
              </Button>
            )}
          </>
        }
      />

      <div className="mb-6 flex items-center gap-3 text-sm">
        <span className={'rounded-full px-2.5 py-0.5 text-xs font-medium ' + SURVEY_STATUS_TONE[survey.status]}>
          {SURVEY_STATUS_LABEL[survey.status]}
        </span>
        {live && <span className="text-slate-400">Auto-refreshing every 10s</span>}
      </div>

      <MetricCards metrics={metrics} onResend={canResend ? () => setAction('resend') : undefined} />
      <ChannelBreakdown email={channels.email} sms={channels.sms} />

      <AudienceFilterBar
        filter={filter}
        options={filterOptions}
        filteredCount={filteredResponses.length}
        totalCount={submitted.length}
        onChange={setFilter}
      />
      <QuestionResults results={questionResults} />

      <IndividualResponses
        recipients={results.data.recipients}
        sends={results.data.sends}
        responses={results.data.responses}
        steps={stepsOf(results.data)}
      />

      {action === 'close' && (
        <ConfirmActionModal
          title="Close this survey?"
          body="The public link will show a closed view and non-responders will be marked expired. Responses already submitted are kept. This can't be undone."
          confirmLabel="Close survey"
          busy={closeMutation.isPending}
          onConfirm={() => closeMutation.mutate()}
          onCancel={() => setAction(null)}
        />
      )}
      {action === 'cancel' && (
        <ConfirmActionModal
          title="Cancel this survey?"
          body="The survey stops accepting new responses and the link shows a closed view. Existing responses are preserved. This can't be undone."
          confirmLabel="Cancel survey"
          destructive
          busy={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate()}
          onCancel={() => setAction(null)}
        />
      )}
      {action === 'resend' && (
        <ResendModal
          survey={survey}
          pending={metrics.pending}
          onClose={() => setAction(null)}
          onDone={() => void results.refetch()}
        />
      )}
    </div>
  );
}

// Trigger a client-side CSV download from a string.
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
